import React, { useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mermaid from 'mermaid';

// Initialize mermaid with dark theme
mermaid.initialize({
  startOnLoad: false,
  theme: 'dark',
  themeVariables: {
    primaryColor: '#00d4aa',
    primaryTextColor: '#fff',
    primaryBorderColor: '#00d4aa',
    lineColor: '#6b7280',
    secondaryColor: '#1a1a2e',
    tertiaryColor: '#2d2d44',
    background: '#1a1a2e',
    mainBkg: '#1a1a2e',
    secondBkg: '#2d2d44',
    border1: '#00d4aa',
    border2: '#6b7280',
    arrowheadColor: '#00d4aa',
    fontFamily: 'inherit',
    fontSize: '14px',
    textColor: '#e5e7eb',
    nodeTextColor: '#fff'
  },
  flowchart: {
    htmlLabels: true,
    curve: 'basis'
  }
});

// Mermaid code block component
const MermaidDiagram = ({ code }) => {
  const containerRef = useRef(null);
  const [svg, setSvg] = React.useState('');
  const [error, setError] = React.useState(null);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!code || !containerRef.current) return;
      
      try {
        const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
        const { svg } = await mermaid.render(id, code);
        setSvg(svg);
        setError(null);
      } catch (err) {
        console.error('Mermaid render error:', err);
        setError(err.message || 'Erreur de rendu du diagramme');
      }
    };

    renderDiagram();
  }, [code]);

  if (error) {
    return (
      <Box sx={{ 
        p: 2, 
        bgcolor: 'rgba(239, 68, 68, 0.1)', 
        borderRadius: 1,
        border: '1px solid rgba(239, 68, 68, 0.3)'
      }}>
        <Typography variant="body2" color="error">
          Erreur Mermaid: {error}
        </Typography>
        <Box component="pre" sx={{ 
          mt: 1, 
          p: 1, 
          bgcolor: 'rgba(0,0,0,0.2)', 
          borderRadius: 1,
          fontSize: '0.75rem',
          overflow: 'auto'
        }}>
          {code}
        </Box>
      </Box>
    );
  }

  return (
    <Box 
      ref={containerRef}
      sx={{ 
        my: 2,
        p: 2,
        bgcolor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: 2,
        overflow: 'auto',
        '& svg': {
          maxWidth: '100%',
          height: 'auto'
        }
      }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

// Custom code block renderer
const CodeBlock = ({ node, inline, className, children, ...props }) => {
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';
  const code = String(children).replace(/\n$/, '');

  // Handle mermaid diagrams
  if (language === 'mermaid') {
    return <MermaidDiagram code={code} />;
  }

  // Inline code
  if (inline) {
    return (
      <Box
        component="code"
        sx={{
          px: 0.75,
          py: 0.25,
          bgcolor: 'rgba(0, 212, 170, 0.1)',
          borderRadius: 0.5,
          fontFamily: 'monospace',
          fontSize: '0.875em',
          color: '#00d4aa'
        }}
        {...props}
      >
        {children}
      </Box>
    );
  }

  // Code block
  return (
    <Box
      component="pre"
      sx={{
        p: 2,
        my: 2,
        bgcolor: 'rgba(0, 0, 0, 0.3)',
        borderRadius: 1,
        overflow: 'auto',
        fontFamily: 'monospace',
        fontSize: '0.85rem',
        lineHeight: 1.6,
        border: '1px solid rgba(255, 255, 255, 0.06)'
      }}
    >
      <Box component="code" className={className} {...props}>
        {children}
      </Box>
    </Box>
  );
};

// Styled markdown components
const markdownComponents = {
  h1: ({ children }) => (
    <Typography variant="h4" sx={{ mt: 3, mb: 2, fontWeight: 700, color: '#00d4aa' }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="h5" sx={{ mt: 3, mb: 1.5, fontWeight: 600, color: '#00d4aa' }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600, color: 'text.primary' }}>
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600, color: 'text.primary' }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7, color: 'text.secondary' }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 0.5 } }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 3, mb: 2, '& li': { mb: 0.5 } }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
      {children}
    </Typography>
  ),
  blockquote: ({ children }) => (
    <Box
      sx={{
        pl: 2,
        py: 1,
        my: 2,
        borderLeft: '3px solid #00d4aa',
        bgcolor: 'rgba(0, 212, 170, 0.05)',
        borderRadius: '0 4px 4px 0',
        '& p': { mb: 0 }
      }}
    >
      {children}
    </Box>
  ),
  hr: () => (
    <Box
      component="hr"
      sx={{
        my: 3,
        border: 'none',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}
    />
  ),
  a: ({ href, children }) => (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        color: '#00d4aa',
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' }
      }}
    >
      {children}
    </Box>
  ),
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 600, color: 'text.primary' }}>
      {children}
    </Box>
  ),
  em: ({ children }) => (
    <Box component="em" sx={{ fontStyle: 'italic' }}>
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <Box sx={{ overflowX: 'auto', my: 2 }}>
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          '& th, & td': {
            p: 1.5,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            textAlign: 'left'
          },
          '& th': {
            bgcolor: 'rgba(0, 212, 170, 0.1)',
            fontWeight: 600
          },
          '& tr:nth-of-type(even)': {
            bgcolor: 'rgba(255, 255, 255, 0.02)'
          }
        }}
      >
        {children}
      </Box>
    </Box>
  ),
  code: CodeBlock
};

/**
 * MarkdownViewer - Renders markdown content with Mermaid diagram support
 * @param {string} content - Markdown content to render
 * @param {boolean} loading - Show loading state
 */
function MarkdownViewer({ content, loading = false }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress size={32} sx={{ color: '#00d4aa' }} />
      </Box>
    );
  }

  if (!content) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        Aucun contenu à afficher
      </Typography>
    );
  }

  return (
    <Box sx={{ 
      px: 3, 
      py: 2,
      '& > *:first-of-type': { mt: 0 },
      '& > *:last-child': { mb: 0 }
    }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
}

export default MarkdownViewer;
